import type { PanelBrandSlug } from "@/config/config";

import { EProductType } from "./products.type";

/**
 * Lista comercial de tipos de produto (adicionar produto).
 * `implemented`: fluxo de cadastro disponível no painel.
 */
export type TProductCatalogEntry = {
  id: string;
  implemented: boolean;
  /** Quando implementado, valor enviado ao fluxo existente */
  productType?: EProductType;
  /** Chave i18n para o rótulo (sempre `t(labelKey)`) */
  labelKey: string;
};

export const PRODUCT_CATALOG_ENTRIES: readonly TProductCatalogEntry[] = [
  {
    id: EProductType.INSURANCE_AP,
    implemented: true,
    productType: EProductType.INSURANCE_AP,
    labelKey: `product.type.${EProductType.INSURANCE_AP}`,
  },
  {
    id: EProductType.AUTO,
    implemented: true,
    productType: EProductType.AUTO,
    labelKey: `product.type.${EProductType.AUTO}`,
  },
  {
    id: EProductType.MOTO,
    implemented: true,
    productType: EProductType.MOTO,
    labelKey: `product.type.${EProductType.MOTO}`,
  },
  {
    id: "machinery_equipment_insurance",
    implemented: false,
    labelKey: "products.catalog.future.machinery_equipment_insurance",
  },
  {
    id: "heavy_vehicles_insurance",
    implemented: false,
    labelKey: "products.catalog.future.heavy_vehicles_insurance",
  },
  {
    id: "property_insurance",
    implemented: false,
    labelKey: "products.catalog.future.property_insurance",
  },
  {
    id: EProductType.WARRANTY_INSURANCE,
    implemented: true,
    productType: EProductType.WARRANTY_INSURANCE,
    labelKey: `product.type.${EProductType.WARRANTY_INSURANCE}`,
  },
  {
    id: EProductType.THEFT_INSURANCE,
    implemented: true,
    productType: EProductType.THEFT_INSURANCE,
    labelKey: `product.type.${EProductType.THEFT_INSURANCE}`,
  },
  {
    id: "grain_crop_insurance",
    implemented: false,
    labelKey: "products.catalog.future.grain_crop_insurance",
  },
  {
    id: "pme_insurance",
    implemented: false,
    labelKey: "products.catalog.future.pme_insurance",
  },
  {
    id: "civil_liability_insurance",
    implemented: false,
    labelKey: "products.catalog.future.civil_liability_insurance",
  },
  {
    id: "do_insurance",
    implemented: false,
    labelKey: "products.catalog.future.do_insurance",
  },
  {
    id: "risks_insurance",
    implemented: false,
    labelKey: "products.catalog.future.risks_insurance",
  },
  {
    id: "travel_insurance",
    implemented: false,
    labelKey: "products.catalog.future.travel_insurance",
  },
  {
    id: "mobile_insurance",
    implemented: false,
    labelKey: "products.catalog.future.mobile_insurance",
  },
  {
    id: "credit_line_insurance",
    implemented: false,
    labelKey: "products.catalog.future.credit_line_insurance",
  },
  {
    id: EProductType.CREDIT,
    implemented: true,
    productType: EProductType.CREDIT,
    labelKey: `product.type.${EProductType.CREDIT}`,
  },
  {
    id: EProductType.INSURANCE_ASSISTANCE,
    implemented: true,
    productType: EProductType.INSURANCE_ASSISTANCE,
    labelKey: `product.type.${EProductType.INSURANCE_ASSISTANCE}`,
  },
] as const;

/** Seguro VG: não aparece na imagem do cliente, mantido no catálogo após AP. */
export const PRODUCT_CATALOG_VG_ENTRY: TProductCatalogEntry = {
  id: EProductType.INSURANCE_VG,
  implemented: true,
  productType: EProductType.INSURANCE_VG,
  labelKey: `product.type.${EProductType.INSURANCE_VG}`,
};

/**
 * Retorna as entradas do catálogo aplicáveis ao painel atual.
 *
 * - **SoulHub** (`panelBrand === 'soulhub'`): sempre exibe o catálogo completo
 *   (tipos com formulário + placeholders "em breve"). Não depende da contagem
 *   da API — antes falhávamos ao comparar 10 tipos da API com 8 valores do
 *   enum local.
 * - **Infinity** (`panelBrand === 'infinity'`): só tipos implementados cujo
 *   `productType` está em `allowedTypes` (tipicamente AP, VG, Assistências).
 * - Enquanto `allowedTypes` carrega no Infinity, devolve lista vazia para não
 *   exibir opções inválidas.
 */
export function getProductCatalogEntriesForUi(
  allowedTypes: EProductType[] | undefined,
  panelBrand: PanelBrandSlug,
): TProductCatalogEntry[] {
  const [first, ...rest] = PRODUCT_CATALOG_ENTRIES;
  const fullCatalog = [first, PRODUCT_CATALOG_VG_ENTRY, ...rest];

  if (panelBrand === "soulhub") {
    return fullCatalog;
  }

  if (!allowedTypes || allowedTypes.length === 0) {
    return [];
  }

  const allowedSet = new Set(allowedTypes);
  return fullCatalog.filter(
    (entry) =>
      entry.productType !== undefined && allowedSet.has(entry.productType),
  );
}
